import { ColorVariantsItem } from 'components/Pages/ProductDetail/ColorVariants/ColorVariantsItem';
import { useComponentUpdate } from 'hooks/helpers/UseComponentUpdate';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { MinusIcon, PlusIcon } from 'public/svg/IconsSvg';
import { useCallback, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { ColorVariantType } from 'types/product';

type Props = {
    colorVariants: ColorVariantType[];
    currentVariantUuid: string;
};

const MAX_DESKTOP_VARIANTS_VISIBLE = 4;

export const ColorVariantsList: FC<Props> = ({ colorVariants, currentVariantUuid }) => {
    const t = useTypedTranslationFunction();

    const [areAllVariantsVisible, setAllVariantsVisibility] = useState(
        colorVariants.length <= MAX_DESKTOP_VARIANTS_VISIBLE,
    );

    const onToggleVariantsVisibilityHandler = useCallback(() => setAllVariantsVisibility((prev) => !prev), []);

    const getLastVisibleIndex = useMemo(() => {
        if (areAllVariantsVisible) {
            return Number.MAX_SAFE_INTEGER;
        }

        if (colorVariants.length <= MAX_DESKTOP_VARIANTS_VISIBLE) {
            return MAX_DESKTOP_VARIANTS_VISIBLE - 1;
        }

        return MAX_DESKTOP_VARIANTS_VISIBLE - 2;
    }, [areAllVariantsVisible, colorVariants.length]);

    useComponentUpdate(
        () => setAllVariantsVisibility(colorVariants.length <= MAX_DESKTOP_VARIANTS_VISIBLE),
        [colorVariants.length],
    );

    return (
        <div
            className={twJoin(
                '-mx-1 mb-6 flex overflow-hidden p-1',
                areAllVariantsVisible ? 'flex-wrap' : 'flex-nowrap',
            )}
        >
            {colorVariants.map(
                (colorVariant, index) =>
                    index <= getLastVisibleIndex && (
                        <ColorVariantsItem
                            variant={colorVariant}
                            currentVariantUuid={currentVariantUuid}
                            key={colorVariant.uuid}
                        />
                    ),
            )}

            {colorVariants.length > MAX_DESKTOP_VARIANTS_VISIBLE && (
                <button
                    className="mx-1 mb-2 flex w-[calc(100%/4-10px)] cursor-pointer flex-col items-center justify-center rounded-md border-0 bg-gray p-1 text-small font-normal lowercase transition-all duration-200 ease-defaultTransition hover:bg-grayMidDarker"
                    onClick={onToggleVariantsVisibilityHandler}
                >
                    {areAllVariantsVisible ? (
                        <MinusIcon className="mb-3 h-5 w-5" />
                    ) : (
                        <PlusIcon className="mb-3 h-5 w-5" />
                    )}
                    {areAllVariantsVisible ? t('show less') : t('show more')}
                </button>
            )}
        </div>
    );
};
